import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';
import { userService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { CrisisProviderNode } from 'src/generated/webapp_gql';
import { getSortedCountries } from 'src/modules/countries/selectors/countrySelectors';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { CountryInput } from 'src/shared/components/CountryInput';
import { Modal } from 'src/shared/components/Modal';
import { color, font } from 'src/shared/styles/variables';
import helpIcon from 'src/shared/svg/icon-help.svg';
import phoneIcon from 'src/shared/svg/icon-phone.svg';

interface StyledIcon {
    iconUrl: string;
}

export const StyledIcon = styled('i')<StyledIcon>(({ iconUrl }) => ({
    backgroundImage: `url('${iconUrl}')`,
    width: '1.75rem',
    height: '1.75rem',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
}));

const StyledModal = styled(Modal)({
    '& .ant-modal-content': {
        margin: '0 auto',

        '& .ant-modal-body': {
            padding: '3.50rem 4.38rem 2.63rem',
        },
    },
});

const HeadingText = styled.h3(
    {
        color: color.pickledBluewood,
        textAlign: 'left',
    },
    font.heavy(font.size.header),
);

const DescriptionText = styled.p(
    {
        color: color.blueBayoux,
        textAlign: 'left',
    },
    font.medium(font.size.medium),
);

const ContentWrapper = styled.div({
    maxWidth: 700,
});

const NumberContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginTop: 25,
});

const ResourceContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: 25,
});

const ResourceSubContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '0.88rem',
});

const ResourceLabel = styled.p(
    {
        color: color.pickledBluewood,
    },
    font.heavy(font.size.medium),
);

const ResourceLink = styled.a(
    {
        color: color.royalBlue,
        textDecoration: 'underline',
    },
    font.medium(font.size.medium),
);

const LabelContainer = styled.div({
    display: 'flex',
    flexFlow: 'row',
    alignItems: 'center',
    marginBottom: 16,
});

const InfoContainer = styled.div(
    {
        width: '100%',
        borderRadius: 5,
        padding: '15px 12px',
        backgroundColor: color.lightSteelBlue,
        color: color.blueBayoux,
        p: {
            lineHeight: 1.8,
        },
    },
    font.medium(font.size.base),
);

const Label = styled.p(
    {
        color: color.pickledBluewood,
        marginLeft: '0.88rem',
        marginRight: '0.26rem',
    },
    font.heavy(font.size.medium),
);

const Subtitle = styled.p(
    {
        marginTop: 30,
        color: color.pickledBluewood,
    },
    font.black(font.size.mediumExtended),
);

interface Props {
    visible: boolean;
    onClose: () => void;
    source?: string;
}

const urls = {
    internationalSuicideHotlines: 'http://www.suicide.org/international-suicide-hotlines.html',
    internationalHelplines: 'https://togetherweare-strong.tumblr.com/helpline',
    volunteerPhone: 'https://www.befrienders.org/help-and-support',
};

export enum CrisisModalTestID {
    Modal = 'CrisisModalTestID.Modal',
    InternationalSuicideHotlines = 'CrisisModalTestID.InternationalSuicideHotlines',
    InternationalHelplines = 'CrisisModalTestID.InternationalHelplines',
    VolunteerPhone = 'CrisisModalTestID.VolunteerPhone',
}

export const CrisisModal: React.FC<Props> = ({ onClose, visible, source }) => {
    const { t } = useTranslation();
    const user = useSelector(getUserInformation);
    const dispatch = useDispatch<Dispatch>();
    const countries = useSelector(getSortedCountries);

    const [country, setCountry] = useState(user.country);

    const companyCrisisProviders = user.crisisProviderCompanyAssignments;
    const countryCrisisProviders = user.crisisProviderCountryAssignments;
    const showAdditionalProvider = companyCrisisProviders.length > 0;

    const countryPoliceContact = {};
    const countriesSuicideHotlineContact = {};

    countries.map(({ name, policePhoneNumber, suicideHotlineNumber }) => {
        countryPoliceContact[name] = policePhoneNumber;
        countriesSuicideHotlineContact[name] = suicideHotlineNumber;
    });

    const policeContact = (selectedCountry: string) => {
        if (!countryPoliceContact[selectedCountry]) {
            return t('shared:crisis_modal.police_contact.description');
        }

        return countryPoliceContact[selectedCountry || 'United States'];
    };

    const suicideHotline = (selectedCountry: string) => {
        if (!countriesSuicideHotlineContact[selectedCountry]) {
            return t('shared:crisis_modal.suicide_hotline.description');
        }

        return t(countriesSuicideHotlineContact[selectedCountry || 'United States']);
    };

    const companyCrisisProvidersDict = {};

    for (const i in companyCrisisProviders) {
        if (companyCrisisProviders.hasOwnProperty(i)) {
            const provider = companyCrisisProviders[i];
            // Dictionary to store phone numbers by country
            const providerPhoneNumberByCountry = {};
            for (const j in countryCrisisProviders) {
                if (countryCrisisProviders.hasOwnProperty(j)) {
                    const providerCountry = countryCrisisProviders[j];
                    if (
                        providerCountry.crisisProvider.name === provider.crisisProvider.name &&
                        providerCountry.phoneNumber
                    ) {
                        providerPhoneNumberByCountry[providerCountry.country.name] =
                            providerCountry.phoneNumber;
                    }
                }
            }
            companyCrisisProvidersDict[provider.crisisProvider.name] = providerPhoneNumberByCountry;
        }
    }

    const getPhoneNumberByCountryAndProvider = (
        provider: CrisisProviderNode,
        selectedCountry: string,
    ) => {
        return (
            companyCrisisProvidersDict[provider.name][selectedCountry] ??
            provider.defaultPhoneNumber
        );
    };

    useEffect(() => {
        if (visible && source) {
            userService.submitCrisisResourceEvent(source);
        }
    }, [visible]);

    return (
        <React.Fragment>
            <StyledModal
                wrapProps={{
                    'data-testid': CrisisModalTestID.Modal,
                }}
                visible={visible}
                onCancel={onClose}
                footer={null}
                width="auto"
                centered
            >
                <ContentWrapper>
                    <HeadingText>{t(`shared:crisis_modal.header`)}</HeadingText>
                    <DescriptionText>{t(`shared:crisis_modal.description`)}</DescriptionText>

                    <CountryInput
                        onSelectCountry={(value: string) => {
                            setCountry(value);

                            dispatch.user.updateUserInfo({
                                country: value,
                            });
                        }}
                        country={country}
                    />

                    {showAdditionalProvider
                        ? companyCrisisProviders.map(crisisProvider => (
                              <NumberContainer key={crisisProvider.id}>
                                  <LabelContainer>
                                      <StyledIcon iconUrl={helpIcon} />{' '}
                                      <Label>{crisisProvider.crisisProvider.name}:</Label>
                                  </LabelContainer>
                                  <InfoContainer>
                                      <p>
                                          {getPhoneNumberByCountryAndProvider(
                                              crisisProvider.crisisProvider,
                                              country,
                                          )
                                              ? getPhoneNumberByCountryAndProvider(
                                                    crisisProvider.crisisProvider,
                                                    country,
                                                )
                                              : t(
                                                    'shared:crisis_modal.suicide_hotline.description',
                                                )}
                                      </p>
                                  </InfoContainer>
                              </NumberContainer>
                          ))
                        : null}

                    <NumberContainer>
                        <LabelContainer>
                            <StyledIcon iconUrl={helpIcon} />
                            <Label>{t('shared:crisis_modal.police_contact.label')}:</Label>
                        </LabelContainer>
                        <InfoContainer>
                            <p>{policeContact(country)}</p>
                        </InfoContainer>
                    </NumberContainer>

                    <NumberContainer>
                        <LabelContainer>
                            <StyledIcon iconUrl={phoneIcon} />
                            <Label>{t('shared:crisis_modal.suicide_hotline.label')}:</Label>
                        </LabelContainer>
                        <InfoContainer>
                            <p>{suicideHotline(country)}</p>
                        </InfoContainer>
                    </NumberContainer>

                    <Subtitle>{t('shared:crisis_modal.international.label')}</Subtitle>
                    <ResourceContainer>
                        <StyledIcon iconUrl={phoneIcon} />
                        <ResourceSubContainer>
                            <ResourceLabel>
                                {t('shared:crisis_modal.international_suicide_hotlines.title')}
                            </ResourceLabel>
                            <ResourceLink
                                data-testid={CrisisModalTestID.InternationalSuicideHotlines}
                                target="_blank"
                                href={urls.internationalSuicideHotlines}
                            >
                                {urls.internationalSuicideHotlines}
                            </ResourceLink>
                        </ResourceSubContainer>
                    </ResourceContainer>
                    <ResourceContainer>
                        <StyledIcon iconUrl={helpIcon} />
                        <ResourceSubContainer>
                            <ResourceLabel>
                                {t('shared:crisis_modal.international_helplines.title')}
                            </ResourceLabel>
                            <ResourceLink
                                target="_blank"
                                data-testid={CrisisModalTestID.InternationalHelplines}
                                href={urls.internationalHelplines}
                            >
                                {urls.internationalHelplines}
                            </ResourceLink>
                        </ResourceSubContainer>
                    </ResourceContainer>
                    <ResourceContainer>
                        <StyledIcon iconUrl={phoneIcon} />
                        <ResourceSubContainer>
                            <ResourceLabel>
                                {t('shared:crisis_modal.volunteer_phone.title')}
                            </ResourceLabel>
                            <ResourceLink
                                target="_blank"
                                data-testid={CrisisModalTestID.VolunteerPhone}
                                href={urls.volunteerPhone}
                            >
                                {urls.volunteerPhone}
                            </ResourceLink>
                        </ResourceSubContainer>
                    </ResourceContainer>
                </ContentWrapper>
            </StyledModal>
        </React.Fragment>
    );
};
